import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { IRolodexContactDTO, IRolodexContactsSearchParams, IRolodexDAO } from '../../../../../../api/rolodex.dao';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { alertServerError, showNotification } from '../../../../../global/modal/alerts-host/alert-templates';
import { MAPPING_UPDATED_TEXT } from './attendee-mapping-form.texts';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../../../infrastructure/sub-windows';
import { RolodexContactForm } from '../../../shared/rolodex-contact/rolodex-contact-form';
import { EDIT_ROLODEX_CONTACT_POPUP_TITLE_TEXT } from '../../../shared/rolodex-contact/rolodex-contact-text';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../api/roles.dao';

class AttendeeMappingFormViewModel {
    @mobx.observable
    public firstName = '';
    @mobx.observable
    public lastName = '';
    @mobx.observable
    public primaryEmail = '';
    @mobx.observable
    public searching = false;
    @mobx.observable
    public searchPerformed = false;
    @mobx.observable
    public searchResults: IRolodexContactDTO[] = [];
    @mobx.observable
    public mappedContactId: number = null;
    @mobx.observable
    public mappedContact: IRolodexContactDTO = null;
    @mobx.observable
    public selectedContactId: number = null;
    @mobx.observable
    public searchRequested = false;
    @mobx.observable
    public saving = false;
    @mobx.observable
    private readonly rolodexContactIdsToSubWindowPopupsMap: { [contactId: string]: ISubWindowFacade } = {};

    constructor(
        private readonly accountId: number,
        private readonly eventId: number,
        private readonly attendeeId: number,
        contactId: number | null,
        private readonly onMappingUpdated?: (contactId: number | null) => void,
        @IoC.Inject private readonly rolodexDAO: IRolodexDAO = IoC.Container.get(IRolodexDAO),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
    ) {
        this.mappedContactId = contactId ?? null;
        this.selectedContactId = this.mappedContactId;
    }

    @mobx.computed
    public get shouldShowSearch(): boolean {
        return !this.mappedContact || this.searchRequested;
    }

    public initialize = async (): Promise<void> => {
        if (this.mappedContactId == null) return;
        try {
            const contact = await this.rolodexDAO.getContact({ contactId: this.mappedContactId });
            mobx.runInAction(() => {
                this.mappedContact = contact;
            });
        } catch (e) {
            alertServerError('API error fetching mapped contact', this.errorParsingService.extractMessage(e));
        }
    };

    @mobx.action
    public setFirstName = (value: string): void => {
        this.firstName = value;
        this.resetSearchStateOnEmptyCriteria();
    };

    @mobx.action
    public setLastName = (value: string): void => {
        this.lastName = value;
        this.resetSearchStateOnEmptyCriteria();
    };

    @mobx.action
    public setEmail = (value: string): void => {
        this.primaryEmail = value;
        this.resetSearchStateOnEmptyCriteria();
    };

    private resetSearchStateOnEmptyCriteria = (): void => {
        if (this.hasSearchCriteria()) return;
        this.searchPerformed = false;
        this.searchResults = [];
    };

    private hasSearchCriteria = (): boolean => Object.keys(this.buildSearchParams()).length > 0;

    public canSearch = (): boolean => !this.searching && this.hasSearchCriteria();

    private buildSearchParams = (): IRolodexContactsSearchParams => {
        const params: IRolodexContactsSearchParams = {};
        if (this.firstName.trim()) params.firstName = this.firstName.trim();
        if (this.lastName.trim()) params.lastName = this.lastName.trim();
        if (this.primaryEmail.trim()) params.email = this.primaryEmail.trim();
        return params;
    };

    @mobx.action
    public search = async (): Promise<void> => {
        if (!this.canSearch()) return;
        this.searching = true;
        try {
            const result = await this.rolodexDAO.searchContacts(this.buildSearchParams());
            mobx.runInAction(() => {
                this.searchResults = result || [];
                this.searchPerformed = true;
            });
        } catch (e) {
            alertServerError('API error searching contacts', this.errorParsingService.extractMessage(e));
        } finally {
            mobx.runInAction(() => {
                this.searching = false;
            });
        }
    };

    @mobx.action
    public selectContact = (contactId: number): void => {
        this.selectedContactId = this.selectedContactId === contactId ? null : contactId;
    };

    @mobx.action
    public requestRemoval = (): void => {
        this.selectedContactId = null;
        this.searchRequested = true;
    };

    @mobx.action
    public requestChange = (): void => {
        this.searchRequested = true;
    };

    public hasChanges = (): boolean => this.selectedContactId !== this.mappedContactId;

    @mobx.action
    public apply = async (): Promise<boolean> => {
        if (!this.hasChanges()) return true;
        this.saving = true;
        const contactId = this.selectedContactId;
        try {
            await this.rolodexDAO.updateAttendeeMapping({
                accountId: this.accountId,
                eventId: this.eventId,
                attendeeId: this.attendeeId,
                contactId: contactId,
            });
            this.onMappingUpdated?.(contactId);
            showNotification(this.intlProvider.getIntl().formatMessage(MAPPING_UPDATED_TEXT));
            return true;
        } catch (e) {
            alertServerError('API error updating attendee mapping', this.errorParsingService.extractMessage(e));
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    };

    @mobx.action
    public openEditRolodexContactPopup = (contactId: number): void => {
        const alreadyShownSubWindow = this.rolodexContactIdsToSubWindowPopupsMap[contactId];

        let shallOpenNewSubWindow = true;
        if (alreadyShownSubWindow && alreadyShownSubWindow.getStatus() !== SubWindowStatus.Closed) {
            alreadyShownSubWindow.maximize();
            shallOpenNewSubWindow = false;
        }
        if (shallOpenNewSubWindow) {
            this.rolodexContactIdsToSubWindowPopupsMap[contactId] = this.subWindowService.showSubWindow({
                title: this.intlProvider.getIntl().formatMessage(EDIT_ROLODEX_CONTACT_POPUP_TITLE_TEXT),
                componentToHost: RolodexContactForm,
                componentToHostProps: {
                    contactId: contactId,
                    intl: this.intlProvider.getIntl(),
                    saveCompletedCallback: (contact: IRolodexContactDTO) => {
                        this.updateMappedContact(contact);
                        this.updateSearchResults(contact);
                    },
                },
            });
        }
    };

    public hasEditRolodexContactPermission(): boolean {
        return hasPermission(Permission.EDIT_ROLODEX_CONTACT);
    }

    private matchesSearchCriteria = (contact: IRolodexContactDTO): boolean => {
        const firstName = this.firstName.trim();
        const lastName = this.lastName.trim();
        const email = this.primaryEmail.trim();

        if (firstName) {
            const term = firstName.toLowerCase();
            const inFirst = (contact.firstName || '').toLowerCase().includes(term);
            const inPreferred = (contact.preferredName || '').toLowerCase().includes(term);
            if (!inFirst && !inPreferred) return false;
        }
        if (lastName) {
            const term = lastName.toLowerCase();
            if (!(contact.lastName || '').toLowerCase().includes(term)) return false;
        }
        if (email) {
            const term = email.toLowerCase();
            if (!(contact.primaryEmail || '').toLowerCase().includes(term)) return false;
        }
        return true;
    };

    @mobx.action
    private updateSearchResults(contact: IRolodexContactDTO): void {
        const index = this.searchResults.findIndex(c => c.contactId === contact.contactId);
        if (index === -1) return;

        mobx.runInAction(() => {
            if (this.matchesSearchCriteria(contact)) {
                this.searchResults[index] = {
                    ...this.searchResults[index],
                    ...contact
                };
            } else {
                this.searchResults.splice(index, 1);
                if (this.selectedContactId === contact.contactId) {
                    this.selectedContactId = null;
                }
            }
        });
    }

    @mobx.action
    private updateMappedContact(contact: IRolodexContactDTO): void {
        const isMappedContact = this.mappedContact?.contactId === contact.contactId;
        mobx.runInAction(() => {
            if (isMappedContact) {
                this.mappedContact = {
                    ...this.mappedContact,
                    ...contact
                };
            }
        });
    }
}

export { AttendeeMappingFormViewModel };
